/**
 * "Change card": opens the Stripe customer portal in this tab (Stripe sends
 * the customer back to `/settings?tab=billing&portal=done`). PrintRun never
 * sees card data and never frames a Stripe page (handover §6).
 *
 * One instance per Billing tab, shared by every Change card button, so two
 * buttons can never start two sessions.
 *
 * - `retry: false`: the app default retries a failed mutation once, which here
 *   would be a second Stripe call for a refusal that will not change (no
 *   portal settings saved yet, handover A2).
 * - The button keeps spinning after success, until the browser leaves.
 * - A refusal is a destructive toast that stays until dismissed (DESIGN §15.5),
 *   in plain words; the screen never crashes over it.
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { billingErrorKind } from '../lib/billing-errors';

export interface BillingPortal {
  open: () => void;
  /** A session is being made, or the browser is on its way to Stripe. */
  busy: boolean;
}

export function useBillingPortal(): BillingPortal {
  const { t } = useTranslation('billing');
  const [leaving, setLeaving] = useState(false);
  const { mutate, isPending } = trpc.billing.createPortalSession.useMutation({ retry: false });

  const open = useCallback(() => {
    if (isPending || leaving) return;
    mutate(undefined, {
      onSuccess: ({ url }) => {
        if (!url.startsWith('https://')) {
          toast({ variant: 'destructive', title: t('portal.error.unavailable'), duration: Infinity });
          return;
        }
        setLeaving(true);
        window.location.assign(url);
      },
      onError: (error) => {
        const kind = billingErrorKind(error);
        const title =
          kind === 'portal_unavailable'
            ? t('portal.error.unavailable')
            : kind === 'not_set_up'
              ? t('portal.error.notSetUp')
              : t('portal.error.generic');
        toast({ variant: 'destructive', title, duration: Infinity });
      },
    });
  }, [isPending, leaving, mutate, t]);

  return { open, busy: isPending || leaving };
}
